export type InvoiceLineItem = {
  id: string
  lineName: string
  description: string
  rate: number
  quantity: number
  lineTotal: number
  tax: boolean
  position: number
  taxPercentage: number
  taxAmount: number
}

export type Event = {
  id: string
  event: 'SentEFT' | 'InvoiceSent' | 'CommentAdded' | 'SentCheque'
  notes: string
  createdDate: string | null
}

export enum InvoiceEvent {
  SentEFT = 'SentEFT',
  InvoiceSent = 'InvoiceSent',
  CommentAdded = 'CommentAdded',
  SentCheque = 'SentCheque',
}

export type InvoicePayload = {
  userInvoiceId: string
  orderId: string
  userOrderId: string
  loadNumber: string
  userId: string
  customerId: string
  issueDate: string
  dueDate: string
  status: 'TO_BE_INVOICED' | 'NOT_PAID' | 'PAID'
  /** Customer AR invoices. Null/omitted → receivables on get/create/update. */
  invoiceType?: 'receivables' | 'payable_carrier'
  totalAmount: number
  currency: 'CAD' | 'USD'
  batchId?: number | null
  batchDisplayId?: string | null
  lineItems: InvoiceLineItem[]
}

export type InvoiceResponse = {
  id: string
  customerName: string
  createdDate: string | null
  updatedDate: string | null
  events: Event[]
} & InvoicePayload

export type TransformInvoiceResponse = {
  customerName: string
  invoiceData: InvoiceResponse[]
}

/** Main invoice workspace tabs (maps to API status; see getListInvoicesApiStatus). */
export type InvoiceType = 'TO_DELIVER' | 'TO_INVOICE' | 'NOT_PAID' | 'PAID'

export const isToBeInvoicedWorkspaceTab = (type: InvoiceType) =>
  type === 'TO_DELIVER' || type === 'TO_INVOICE'

export type ListInvoiceParams = {
  invoiceType: InvoiceType
  customerId?: string
}

export type UpdateInvoiceStatusPayload = {
  id: string
  status: 'TO_DELIVER' | 'TO_BE_INVOICED' | 'NOT_PAID' | 'PAID'
}

export type CarrierPayableStatus =
  | 'TO_DELIVER'
  | 'TO_BE_INVOICED'
  | 'NOT_PAID'
  | 'PAID'

export type CarrierInvoice = {
  id: string
  orderId: string
  sourceOrderId: number | null
  sourceUserOrderId: string | null
  loadNumber: string | null
  carrierName: string | null
  totalAmount: number
  dueDate: string | null
  status: CarrierPayableStatus
  invoiceType: 'payable_carrier'
  currency: string
}

export type InvoiceEmail = {
  from: string
  to: string
  subject: string
  message: string
  attachments: string
}

export enum InvoiceDuration {
  All = 'ALL',
  Under30 = 'Under 30 Days',
  Over30 = 'Over 30 Days',
  Over45 = 'Over 45 Days',
  Over60 = 'Over 60 Days',
  Over90 = 'Over 90 Days',
}

export type InvoiceDurationType = keyof typeof InvoiceDuration

export interface DeleteEventPayload {
  invoiceId: string
  eventId: string
}

export type DocumentType =
  | 'INVOICE'
  | 'WAYBILL'
  | 'LC'
  | 'BOL'
  | 'POD'
  | 'REMITTANCE'
  | 'PAYMENT_RECEIPT'
  | 'OTHER'

export interface AddDocumentPayload {
  file: File
  orderId: string
  documentType: DocumentType
}

export interface AddDocumentResponse {
  message: string
  fileName: string
  documentType: DocumentType
  persistenceId: string
}

export interface InvoiceDocument {
  documentName: string
  originalName: string
  s3Path: string
}

export type ListDocumentsResponse = {
  [key in string]: InvoiceDocument[]
}

export type TransformedDocument = {
  documentType: DocumentType
} & InvoiceDocument

export interface GetPreSignedUrlPayload {
  s3URL: string
  openInline?: boolean
}

export interface GetPreSignedUrlResponse {
  preSignedURL: string
}

export interface SendInvoiceEmailPayload {
  invoiceId: string
  recipient: string
  subject: string
  message: string
  s3Urls: string[]
}

export interface InvoiceEmailDto {
  invoiceId: string
  recipient: string
  createdAt: string
}

export interface CreateBatchPayload {
  invoiceIds: string[]
  totalAmount: number
}

export interface BatchResponse {
  id: number
  batchId: string
  invoiceIds: string[]
  totalAmount: number
  createdAt: string
  batchS3Url: string
}

export interface SendBatchInvoiceEmailPayload {
  invoiceIds: string[]
  recipient: string
  subject: string
  message: string
  s3Urls: string[]
}